/* MG Cart Drawer. Sends cart requests and swaps in Shopify's fresh render of sections/mg-cart-drawer.liquid.
   No price math here: everything shown comes from Liquid. The checkout button is a plain form,
   so checkout still works if this file fails to load.

   Hooks into Horizon without editing Horizon files:
   - opens on every successful product-form add (Horizon's 'cart:update' event, source 'product-form-component'),
     which covers product pages, quick add and the sticky add-to-cart bar;
   - the header cart icon opens this drawer instead of Horizon's (Horizon's drawer stays in the DOM, never shown);
   - after each change here it fires 'cart:update' (source 'mg-cart-drawer') so the header count and any
     Horizon cart page stay in sync.

   JS API (for custom buy boxes):
     await window.MGCart.open()     re-render from the live cart and open the drawer. Call after your own /cart/add.js.
                                    Rejects if the cart can't be shown, so a caller can fall back to /checkout.
     await window.MGCart.refresh()  re-render without opening (e.g. after a background cart change).
     window.MGCart.close()
   Or, with no JS dependency: document.dispatchEvent(new CustomEvent('mg-cart:open')). */
(() => {
  if (customElements.get('mg-cart-drawer')) return;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const SOURCE = 'mg-cart-drawer';

  class MGCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.dialog = this.querySelector('dialog');
      this.sid = this.dataset.sectionId;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      // click on the backdrop (the dialog itself, outside the panel) closes it
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.sid) this.open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.sid) this.close(); });
      // Back from checkout restores this page from memory with the old cart: re-render so the drawer shows the real one
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.run([]); });

      // Horizon adds: product form, quick add, sticky bar all dispatch this after a successful /cart/add
      document.addEventListener('cart:update', (e) => {
        const d = (e.detail && e.detail.data) || {};
        if (d.source === 'product-form-component' && !d.didError) this.open().catch(() => {});
      });
      document.addEventListener('mg-cart:open', () => this.open().catch(() => {}));

      // Header cart icon: capture phase on window runs before Horizon's document-level on:click handler
      window.addEventListener('click', (e) => {
        if (document.body.classList.contains('template-cart') || location.pathname === root + 'cart') return;
        const t = e.target.closest && e.target.closest('[data-testid="cart-drawer-trigger"], a.action__cart');
        if (!t) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open().catch(() => { location.href = root + 'cart'; });
      }, true);
      // Horizon's own drawer must never auto-open next to this one (theme setting "Auto-open cart drawer")
      document.querySelector('cart-drawer-component')?.removeAttribute('auto-open');
    }

    get inner() { return this.querySelector('[data-mg-inner]'); }

    async open() {
      if (!this.dialog.open) this.dialog.showModal();
      await this.run([], { throwOnError: true });
    }

    refresh() { return this.run([]); }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClick(e) {
      const t = e.target.closest('[data-mg-close], [data-mg-change], [data-mg-add], [data-mg-uncode], [data-mg-steps], [data-mg-toggle], [data-mg-more], [data-mg-less], [data-mg-go]');
      if (!t) return;
      e.preventDefault();
      const d = t.dataset;
      if (t.hasAttribute('data-mg-close')) this.close();
      else if (d.mgChange != null) this.run([{ path: 'cart/change.js', body: { id: d.mgChange, quantity: Number(d.qty) } }]);
      else if (d.mgAdd != null) this.run([{ path: 'cart/add.js', body: { items: [{ id: Number(d.mgAdd), quantity: 1 }] } }]);
      else if (d.mgSteps != null) this.run(JSON.parse(d.mgSteps)); // offer card: steps built in snippets/mg-cd-offer.liquid
      else if (d.mgToggle != null) {
        const drop = t.nextElementSibling;
        drop.hidden = !drop.hidden;
        t.setAttribute('aria-expanded', String(!drop.hidden));
      }
      else if (d.mgMore != null || d.mgLess != null) this.pick(t, d.mgMore != null ? 1 : -1);
      else if (d.mgGo != null) this.run(this.pickSteps(t.closest('[data-mg-pick]')));
      else if (d.mgUncode != null) this.run([{ path: 'cart/update.js', body: { discount: this.codes().filter((c) => c !== d.mgUncode).join(',') } }]);
    }

    // Offer card flavor picker (snippets/mg-cd-offer.liquid): +1 / -1 bag of a flavor, at most data-max bags in all.
    picks(box) { return [...box.querySelectorAll('[data-mg-flv]')].map((f) => [Number(f.dataset.mgFlv), Number(f.dataset.n || 0)]); }

    pick(t, delta) {
      const box = t.closest('[data-mg-pick]');
      const fl = t.closest('[data-mg-flv]');
      const total = this.picks(box).reduce((sum, [, n]) => sum + n, 0);
      if (delta > 0 && total >= Number(box.dataset.max)) return;
      const n = Number(fl.dataset.n || 0) + delta;
      fl.dataset.n = n;
      fl.classList.toggle('is-on', n > 0);
      const less = fl.querySelector('[data-mg-less]');
      less.hidden = n === 0;
      less.textContent = 'x' + n;
      this.pickShow(box, total + delta);
    }

    // Price math mirrors the snippet: was = bags x one-time price + kit value, new = bags x plan price.
    pickPlan(box, total) {
      const c = JSON.parse(box.dataset.mgPick);
      const bags = c.bags + total;
      return { c, bags, plan: c.p2 && bags === 2 ? c.p2 : c.p3 };
    }

    pickShow(box, total) {
      const d = box.dataset;
      const fill = (s) => s.replace('[n]', total);
      box.querySelector('.mg-cd__picked').textContent = total === 0 ? d.none : total === 1 ? d.one : fill(d.many);
      const go = box.querySelector('[data-mg-go]');
      go.disabled = total === 0;
      go.textContent = total === 0 ? d.cta.replace('[n] ', '') : total === 1 ? d.ctaOne : fill(d.cta);
      if (!total) return;
      const { c, bags, plan } = this.pickPlan(box, total);
      const was = bags * c.one + c.kitc;
      const now = bags * plan[1];
      const money = (cents) => '$' + (cents / 100).toFixed(2);
      const card = box.closest('.mg-cd__offer');
      card.querySelector('[data-mg-was]').textContent = money(was);
      card.querySelector('[data-mg-new]').textContent = money(now);
      const pct = Math.floor(((was - now) * 100) / was);
      card.querySelector('[data-mg-text]').textContent = d.tpl.replace('[bags]', bags).replace('[pct]', pct);
      const head = card.querySelector('[data-mg-pct]');
      if (head) head.textContent = pct;
    }

    pickSteps(box) {
      const picks = this.picks(box).filter(([, n]) => n > 0);
      const { c, plan } = this.pickPlan(box, picks.reduce((sum, [, n]) => sum + n, 0));
      const items = [];
      for (let i = 0; i < c.keep.length; i += 2) items.push({ id: c.keep[i], quantity: c.keep[i + 1], selling_plan: plan[0] });
      for (const [id, n] of picks) items.push({ id, quantity: n, selling_plan: plan[0] });
      return [c.drop, { path: 'cart/add.js', body: { items: [...items, ...c.kit] } }];
    }

    codes() { return [...this.querySelectorAll('.mg-cd__codes [data-code]')].map((li) => li.dataset.code); }

    onSubmit(e) {
      if (e.target.matches('[data-mg-checkout]')) return this.onCheckout(e);
      if (!e.target.matches('[data-mg-code]')) return;
      e.preventDefault();
      const code = e.target.elements.code.value.trim();
      if (!code) return;
      const have = this.codes();
      if (have.some((c) => c.toLowerCase() === code.toLowerCase())) return;
      this.run([{ path: 'cart/update.js', body: { discount: [...have, code].join(',') }, code }]);
    }

    // Last check before checkout, read straight from /cart.js so it works even if a render failed:
    // Welcome Gifts never reach checkout without a subscription. Capped in time; checkout always happens.
    async onCheckout(e) {
      e.preventDefault();
      e.target.querySelector('button')?.setAttribute('aria-busy', 'true');
      try {
        for (let i = 0; this.busy && i < 50; i++) await new Promise((r) => setTimeout(r, 100));
        const cart = await fetch(root + 'cart.js', { signal: AbortSignal.timeout(4000) }).then((r) => r.json());
        const gifts = cart.items.filter((x) => x.properties && x.properties._welcome_kit);
        if (gifts.length && !cart.items.some((x) => x.selling_plan_allocation)) {
          await fetch(root + 'cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: Object.fromEntries(gifts.map((g) => [g.key, 0])) }), signal: AbortSignal.timeout(4000),
          });
        }
      } catch (err) {
        console.error('MG cart drawer: pre-checkout check skipped', err);
      }
      location.href = root + 'checkout';
    }

    // Runs cart steps one by one, re-renders, then applies the gift fix (at most 2 passes, so it can never loop).
    async run(steps, { throwOnError = false } = {}) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      let error = '';
      const before = this.inner.dataset.count;
      try {
        try {
          if (!steps.length) await this.send(null);
          for (const step of steps) await this.send(step);
        } catch (err) {
          if (throwOnError) throw err;
          console.error('MG cart drawer:', err);
          error = err.message || this.dataset.error;
          // the cart may have changed even though the render didn't arrive: re-sync so the drawer shows the truth
          try { await this.send(null); } catch {}
        }
        for (let i = 0; i < 2; i++) {
          const raw = this.inner.dataset.mgFix;
          if (!raw) break;
          await this.send({ path: 'cart/update.js', body: JSON.parse(raw) });
        }
        const bad = steps.find((s) => s.code && s.applicable === false);
        if (bad) {
          // Shopify keeps unknown codes on the cart as "not applicable": drop it again and say why
          await this.send({ path: 'cart/update.js', body: { discount: this.codes().filter((c) => c.toLowerCase() !== bad.code.toLowerCase()).join(',') } });
          error = this.dataset.badCode;
        }
      } catch (err) {
        console.error('MG cart drawer:', err);
        error = error || err.message || this.dataset.error;
        if (throwOnError) throw err;
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        const box = this.querySelector('[data-mg-err]');
        if (box) { box.textContent = error; box.hidden = !error; }
        if (this.inner.dataset.count !== before || steps.length) this.announce();
      }
    }

    // Tell Horizon (header count, cart page, product forms) the cart changed. Count is absolute here.
    announce() {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { sourceId: SOURCE, data: { source: SOURCE, itemCount: Number(this.inner.dataset.count), sections: {} } },
      }));
    }

    // One request. step = null re-renders the section only.
    async send(step) {
      const sectionsUrl = location.pathname + location.search;
      const page = new URL(location.href);
      page.hash = '';
      page.searchParams.set('sections', this.sid);
      const url = step ? root + step.path : page.pathname + page.search;
      const opts = step
        ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ ...step.body, sections: this.sid, sections_url: sectionsUrl }),
          }
        : {}; // no Accept: application/json here: Shopify then answers {} instead of the section
      let res;
      try {
        res = await fetch(url, opts);
      } catch {
        throw new Error(this.dataset.error);
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || this.dataset.error);
      if (step && step.code && Array.isArray(data.discount_codes)) {
        const hit = data.discount_codes.find((c) => c.code.toLowerCase() === step.code.toLowerCase());
        step.applicable = Boolean(hit && hit.applicable);
      }
      const html = step ? data.sections && data.sections[this.sid] : data[this.sid];
      if (html) this.swap(html);
      else if (step) await this.send(null); // render missing from the response: fetch it on its own
      else throw new Error(this.dataset.error);
    }

    swap(html) {
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mg-inner]');
      if (!next) throw new Error(this.dataset.error);
      const fid = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-fid]');
      const body = this.inner.querySelector('.mg-cd__body');
      const top = body ? body.scrollTop : 0;
      this.inner.replaceWith(next);
      const nb = this.inner.querySelector('.mg-cd__body');
      if (nb) nb.scrollTop = top;
      // keep keyboard focus on the same control after the re-render
      if (fid && this.dialog.open) {
        const again = this.inner.querySelector(`[data-fid="${CSS.escape(fid.dataset.fid)}"]`);
        (again || this.inner.querySelector('[data-mg-close]')).focus();
      }
    }
  }

  customElements.define('mg-cart-drawer', MGCartDrawer);

  const el = () => document.querySelector('mg-cart-drawer');
  window.MGCart = {
    open: () => (el() ? el().open() : Promise.reject(new Error('MG cart drawer missing'))),
    refresh: () => (el() ? el().refresh() : Promise.resolve()),
    close: () => el()?.close(),
  };
})();
